import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

interface AnimatedFolderContentProps {
  isExpanded: boolean;
  children: React.ReactNode;
}

const AnimatedFolderContent: React.FC<AnimatedFolderContentProps> = ({
  isExpanded,
  children,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) {
      return;
    }
    let raf = 0;
    const updateHeight = () => {
      if (contentRef.current) {
        const contentHeight = contentRef.current.scrollHeight;
        setHeight(contentHeight);
      }
    };
    updateHeight();
    const resizeObserver = new ResizeObserver(() => {
      // Defer reads/writes to the next frame so we don't run synchronous layout
      // inside the observer callback (avoids Chromium's ResizeObserver loop error).
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = 0;
        updateHeight();
      });
    });

    resizeObserver.observe(el);

    return () => {
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
    };
  }, [children, isExpanded]);

  return (
    <motion.div
      initial={false} // Don't animate on mount
      animate={{ height: isExpanded ? height : 0 }}
      transition={{
        height: {
          type: 'tween',
          duration: 0.1,
          ease: 'linear',
        },
      }}
      style={{ overflow: 'hidden' }}
    >
      <div ref={contentRef}>{children}</div>
    </motion.div>
  );
};

export { AnimatedFolderContent };
